import React from 'react';
import { Line } from 'react-konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { denormalizePoints } from '../../hooks/useCoordinates';
import { Area, Rack, Shelf, Table, CheckoutZoneData, PolygonEntityType } from '../../app/appContext';
import { CHECKOUT_TYPE_TO_ENTITY, POLYGON_COLORS, isFourPointType } from '../../utils/polygonTypes';
import EditHandles from './layers/EditHandles';
import { gutterPx } from '../../utils/canvasGutter';

type PolygonType = PolygonEntityType;

interface PolygonLayerProps {
  imageSize: { width: number; height: number };
  scale: number;
  handlePxRadius: number; // desired on-screen radius of vertex handles in CSS pixels
  mode: string;
  selectedPolygon: string | null;

  // Editing props
  editingPolygons: { id: string; type: PolygonType; points: [number, number][] }[];
  selectedPoints: { polygonId: string; type: PolygonType; idx: number }[];
  onPointSelect: (polygonId: string, type: PolygonType, idx: number, extend: boolean) => void;
  onPointDragStart: (polygonId: string, type: PolygonType, idx: number, pos: { x: number; y: number }) => void;
  onPointDrag: (polygonId: string, idx: number, pos: { x: number; y: number }) => void;
  onPolygonDragStart: (polygonId: string, type: PolygonType, pos: { x: number; y: number }) => void;
  onPolygonDragMove: (polygonId: string, type: PolygonType, pos: { x: number; y: number }) => void;
  onPolygonDrag: (pos: { x: number; y: number }) => void;
  onPointDragEnd: () => void;
  onPolygonClick: (polygonId: string, type: PolygonType, extend: boolean, normalizedPos: { x: number; y: number } | null) => void;

  // Data props
  standaloneAreas: Area[];
  filteredRacks: Rack[];
  filteredShelves: Shelf[];
  filteredTables: Table[];
  filteredCheckoutZones: CheckoutZoneData[];
  getAreaPoints: (areaId: string) => [number, number][];

  // Shelf creation props
  awaitingRackSelection: boolean;
  onRackClickForShelf: (rackId: string) => void;
}

const TYPE_COLOR: Record<PolygonType, string> = POLYGON_COLORS;

/** Uniform polygon style — keeps the type's identity color in every state and
 *  modulates thickness/dash/fill instead of flipping hue. We deliberately
 *  avoid Konva drop-shadows here: shadowBlur is rendered via offscreen
 *  Gaussian blur every frame, and on a busy canvas with multiple editing
 *  polygons that's the dominant per-frame cost. Use thicker strokes to
 *  signal selection/editing instead. */
/** Widths/dash are in SCREEN px: the Stage is scaled by `scale` (fit-to-box),
 *  so everything is divided by it — otherwise a 4K frame shrunk to the panel
 *  drew hairlines and a small frame drew fat lines (Alexey #430). */
function getPolygonStyle(type: PolygonType, isSelected: boolean, isEditing: boolean, scale: number) {
  const color = TYPE_COLOR[type];
  const fillAlpha = isEditing ? '38' : isSelected ? '26' : '14';
  const k = 1 / (scale || 1);
  return {
    stroke: color,
    selectedStroke: color,
    strokeWidth: (isEditing ? 3.5 : isSelected ? 3 : 2) * k,
    selectedStrokeWidth: (isEditing ? 3.5 : 3) * k,
    dash: isSelected || isEditing ? undefined : [10 * k, 6 * k],
    fill: `${color}${fillAlpha}`,
  };
}

function getNormalizedClickPos(e: KonvaEventObject<MouseEvent>, imageSize: { width: number; height: number }): { x: number; y: number } | null {
  const stage = e.target.getStage();
  if (!stage) return null;
  const pointer = stage.getPointerPosition();
  if (!pointer) return null;
  const stageScale = stage.scaleX() || 1;
  const g = gutterPx(imageSize);
  return {
    x: (pointer.x / stageScale - g.x) / imageSize.width,
    y: (pointer.y / stageScale - g.y) / imageSize.height,
  };
}

const PolygonLayer: React.FC<PolygonLayerProps> = ({
  imageSize,
  scale,
  handlePxRadius,
  mode,
  selectedPolygon,
  editingPolygons,
  selectedPoints,
  onPointSelect,
  onPointDragStart,
  onPointDrag,
  onPolygonDragStart,
  onPolygonDragMove,
  onPolygonDrag,
  onPointDragEnd,
  onPolygonClick,
  standaloneAreas,
  filteredRacks,
  filteredShelves,
  filteredTables,
  filteredCheckoutZones,
  getAreaPoints,
  awaitingRackSelection,
  onRackClickForShelf
}) => {
  const getEditingPoints = (id: string, type: PolygonType) => {
    const poly = editingPolygons.find(p => p.id === id && p.type === type);
    return poly ? poly.points : null;
  };

  const getSelectedPointIndices = (id: string, type: PolygonType) => {
    return selectedPoints
      .filter(p => p.polygonId === id && p.type === type)
      .map(p => p.idx);
  };

  // Клик с зажатым cmd (Mac) / ctrl (Win/Linux) добавляет полигон в выделение;
  // shift оставляем — им уже можно было собирать несколько полигонов в правку.
  const isExtendEvent = (e: KonvaEventObject<MouseEvent>) =>
    e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey;

  const renderPolygonLine = (
    id: string,
    type: PolygonType,
    basePoints: [number, number][],
    onClick: (e: KonvaEventObject<MouseEvent>) => void,
    keySuffix?: string,
    extraLineProps?: Record<string, any>
  ) => {
    const editPts = getEditingPoints(id, type);
    const pts = editPts ?? basePoints;
    if (!pts || pts.length === 0) return null;
    const isSelected = selectedPolygon === id;
    const isEditing = Boolean(editPts);
    const style = getPolygonStyle(type, isSelected, isEditing, scale);
    const key = `${type}_${id}${keySuffix ? `_${keySuffix}` : ''}`;
    return (
      <Line
        key={key}
        points={denormalizePoints(pts, imageSize).flat()}
        closed
        stroke={isSelected ? style.selectedStroke : style.stroke}
        strokeWidth={isSelected ? style.selectedStrokeWidth : style.strokeWidth}
        dash={style.dash}
        fill={style.fill}
        lineJoin="round"
        onClick={onClick}
        draggable={isEditing}
        // Геометрия фигуры живёт в `points`, а сам узел всегда стоит в (0,0).
        // Поэтому e.target.x()/y() — это НЕ позиция курсора, а смещение узла от
        // начала перетаскивания. Раньше здесь был onDragMove, который на каждом
        // кадре отдавал это смещение в состояние и тут же возвращал узел в ноль:
        // Konva успевала нарисовать фигуру под курсором, следом срабатывал
        // возврат, и фигура металась между исходным местом и курсором. Вдобавок
        // из-за возврата каждое событие сообщало смещение от ПРЕДЫДУЩЕГО события,
        // а не от начала, поэтому points сдвигались на крохи и зона фактически
        // никуда не ехала. Сравнение: точки в EditHandles.tsx рисуются по
        // абсолютным координатам, там e.target.x() — настоящая позиция, возврата
        // нет, и они всегда таскались нормально.
        //
        // Теперь во время перетаскивания фигуру двигает сама Konva (без
        // перерисовки React на каждое движение мыши), а состояние обновляется
        // один раз на отпускании. startPos фиксируется как (0,0), поэтому
        // смещение на onDragEnd и есть полная дельта от начала.
        onDragStart={() => {
          onPolygonDragStart(id, type, { x: 0, y: 0 });
        }}
        // Во время перетаскивания Konva двигает только схваченный узел; остальные
        // выделенные полигоны едут за ним через состояние (см. handlePolygonDragMove).
        // Для одиночного выделения обработчик делает ранний выход — прежний путь
        // без перерисовки на каждый кадр сохраняется.
        onDragMove={(e) => {
          onPolygonDragMove(id, type, { x: e.target.x() / imageSize.width, y: e.target.y() / imageSize.height });
        }}
        onDragEnd={(e) => {
          const nx = e.target.x() / imageSize.width;
          const ny = e.target.y() / imageSize.height;
          // Сначала переносим смещение в points, потом возвращаем узел в ноль —
          // иначе узел остался бы смещённым поверх уже сдвинутой геометрии.
          onPolygonDrag({ x: nx, y: ny });
          e.target.position({ x: 0, y: 0 });
          onPointDragEnd();
        }}
        {...extraLineProps}
      />
    );
  };

  const isShelfCreation = mode === 'create_shelf' && awaitingRackSelection;

  return (
    <>
      {/* Pass 1 — polygon outlines, in render order. */}
      {standaloneAreas.map(area => (
        renderPolygonLine(
          area.id, 'standalone', area.points,
          (e) => onPolygonClick(area.id, 'standalone', isExtendEvent(e), getNormalizedClickPos(e, imageSize)),
        )
      ))}

      {filteredRacks.map(rack => {
        const basePts = getAreaPoints(rack.area);
        return renderPolygonLine(
          rack.id, 'rack', basePts,
          (e) => {
            if (isShelfCreation) onRackClickForShelf(rack.id);
            else onPolygonClick(rack.id, 'rack', isExtendEvent(e), getNormalizedClickPos(e, imageSize));
          },
          undefined,
          isShelfCreation ? { shadowForStrokeEnabled: true, shadowColor: '#FFD600', shadowBlur: 12 / (scale || 1), shadowOpacity: 0.8 } : undefined,
        );
      })}

      {filteredShelves.map(shelf => (
        renderPolygonLine(
          shelf.id, 'shelf', getAreaPoints(shelf.area),
          (e) => onPolygonClick(shelf.id, 'shelf', isExtendEvent(e), getNormalizedClickPos(e, imageSize)),
        )
      ))}

      {filteredTables.map(table => (
        renderPolygonLine(
          table.id, 'table', getAreaPoints(table.area),
          (e) => onPolygonClick(table.id, 'table', isExtendEvent(e), getNormalizedClickPos(e, imageSize)),
        )
      ))}

      {filteredCheckoutZones.map(cz => {
        const entityType = CHECKOUT_TYPE_TO_ENTITY[cz.type] || 'queue';
        return cz.areas.map(areaId => {
          const basePts = getAreaPoints(areaId);
          if (basePts.length === 0) return null;
          return renderPolygonLine(
            cz.id, entityType, basePts,
            (e) => onPolygonClick(cz.id, entityType, isExtendEvent(e), getNormalizedClickPos(e, imageSize)),
            `area_${areaId}`,
          );
        });
      })}

      {/* Pass 2 — vertex handles always on top of every polygon. */}
      {editingPolygons.map(poly => (
        <EditHandles
          key={`handles_${poly.type}_${poly.id}`}
          editingPoints={poly.points}
          imageSize={imageSize}
          scale={scale}
          handlePxRadius={handlePxRadius}
          accentColor={TYPE_COLOR[poly.type]}
          markStart={isFourPointType(poly.type)}
          selectedPointIndices={getSelectedPointIndices(poly.id, poly.type)}
          onPointSelect={(idx, ext) => onPointSelect(poly.id, poly.type, idx, ext)}
          onPointDragStart={(idx, pos) => onPointDragStart(poly.id, poly.type, idx, pos)}
          onPointDrag={(idx, pos) => onPointDrag(poly.id, idx, pos)}
          onPointDragEnd={onPointDragEnd}
        />
      ))}
    </>
  );
};

// Memoized so hover-edge state changes in the parent don't force this whole
// pass to re-render (50+ Konva Lines + new lambda callbacks per polygon was
// the dominant cost during cursor moves).
export default React.memo(PolygonLayer);
